import {Routes} from '@angular/router';
import {authGuard} from '@/app/core/guards/auth.guard';
import {guestGuard} from '@/app/core/guards/guest.guard';
import {tenantOnlyGuard} from '@/app/core/guards/tenant.guard';
import {clientOnlyGuard} from '@/app/core/guards/client-only.guard';
import {redirectToOwnSiteGuard} from '@/app/features/landing-builder/guards/redirect-to-own-site.guard';
import {clientFormUnsavedGuard} from '@/app/features/clients/guards/client-form-unsaved.guard';

export const routes: Routes = [
  {path: '', redirectTo: 'home', pathMatch: 'full'},
  {
    // New Landing Page Builder — public preview. Deliberately a separate top-level
    // path from `/lp/:organizationName` (today's live feature) so the two can coexist.
    path: 'site',
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [redirectToOwnSiteGuard],
        loadComponent: () =>
          import('@/app/features/landing-builder/public/site-own-redirect').then(
            (m) => m.SiteOwnRedirectComponent,
          ),
      },
      {
        path: ':organizationName',
        loadComponent: () =>
          import('@/app/features/landing-builder/public/landing-builder-public-page').then(
            (m) => m.LandingBuilderPublicPageComponent,
          ),
      },
      {
        path: ':organizationName/:slug',
        loadComponent: () =>
          import('@/app/features/landing-builder/public/landing-builder-public-page').then(
            (m) => m.LandingBuilderPublicPageComponent,
          ),
      },
    ],
  },
  {
    path: 'lp',
    children: [
      {
        path: ':organizationName/register',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/login-client/login-client').then(
            (m) => m.LoginClient,
          ),
      },
      {
        path: ':organizationName/login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/login-client/login-client').then(
            (m) => m.LoginClient,
          ),
      },
      {
        path: ':organizationName/driver-login',
        loadComponent: () =>
          import('@/app/features/drivers/driver-login/driver-login').then(
            (m) => m.DriverLogin,
          ),
        data: {title: 'drivers.portal.title'},
      },
      {
        path: ':organizationName/driver-scan',
        loadComponent: () =>
          import('@/app/features/drivers/driver-scan/driver-scan').then(
            (m) => m.DriverScan,
          ),
        data: {title: 'drivers.portal.scanTitle'},
      },
      {
        path: ':organizationName/auth/login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/login-client/login-client').then(
            (m) => m.LoginClient,
          ),
      },
      {
        path: ':organizationName/auth/register',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/login-client/login-client').then(
            (m) => m.LoginClient,
          ),
      },
      {
        path: ':organizationName/forgot-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/forgot-password-client/forgot-password-client').then(
            (m) => m.ForgotPasswordClient,
          ),
      },
      {
        path: ':organizationName/auth/reset-password/:token',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/reset-password-client/reset-password-client').then(
            (m) => m.ResetPasswordClient,
          ),
      },
      {
        path: ':organizationName/otp',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('@/app/features/auth/pages/otp-client/otp-client').then(
            (m) => m.OtpClient,
          ),
      },
      {
        path: ':organizationName',
        loadComponent: () =>
          import('@/app/features/landing-pages/public/landing-public').then(
            (m) => m.LandingPublicComponent,
          ),
      },
    ],
  },
  {
    path: 'auth',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('@/app/features/auth/auth-layout/auth-layout').then(
        (m) => m.AuthLayout,
      ),
    children: [
      {path: '', redirectTo: 'login', pathMatch: 'full'},
      {
        path: 'login',
        loadComponent: () =>
          import('@/app/features/auth/pages/login/login').then((m) => m.Login),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('@/app/features/auth/pages/forgot-password/forgot-password').then(
            (m) => m.ForgotPassword,
          ),
      },
      {
        path: 'reset-password/:token',
        loadComponent: () =>
          import('@/app/features/auth/pages/reset-password/reset-password').then(
            (m) => m.ResetPassword,
          ),
      },
      {
        path: 'signup',
        loadComponent: () =>
          import('@/app/features/auth/pages/signup/signup').then(
            (m) => m.Signup,
          ),
      },
      {
        path: 'otp',
        loadComponent: () =>
          import('@/app/features/auth/pages/otp/otp').then((m) => m.Otp),
      },
    ],
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('@/app/shared/layout/layout/layout').then((m) => m.Layout),
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full',
      },
      {
        path: 'home',
        loadComponent: () =>
          import('@/app/shared/layout/layout-home/layout-home').then(
            (m) => m.LayoutHome,
          ),
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/users/users-list/users-list').then(
                (m) => m.UsersList,
              ),
            data: {title: 'dashboard.users'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/users/user-form/user-form').then(
                (m) => m.UserForm,
              ),
            data: {title: 'users.addNewUser', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/users/user-form/user-form').then(
                (m) => m.UserForm,
              ),
            data: {title: 'users.editUser', mode: 'edit'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/users/user-view/user-view').then(
                (m) => m.UserView,
              ),
            data: {title: 'users.viewUser', mode: 'view'},
          },
        ],
      },
      {
        path: 'permissions',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/permissions/permissions-list/permissions-list').then(
                (m) => m.PermissionsList,
              ),
            data: {title: 'permissions.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/permissions/role-form/role-form').then(
                (m) => m.RoleForm,
              ),
            data: {title: 'permissions.addNewRole', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/permissions/role-form/role-form').then(
                (m) => m.RoleForm,
              ),
            data: {title: 'permissions.editRole', mode: 'edit'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/permissions/role-view/role-view').then(
                (m) => m.RoleView,
              ),
            data: {title: 'permissions.viewRole', mode: 'view'},
          },
        ],
      },
      {
        path: 'categories',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/categories/categories/categories').then(
                (m) => m.Categories,
              ),
            data: {title: 'categories.title'},
          },
        ],
      },
      {
        path: 'tags',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/tags/tags/tags').then((m) => m.Tags),
            data: {title: 'tags.title'},
          },
        ],
      },
      {
        path: 'products',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/products/products-list/products-list').then(
                (m) => m.ProductsList,
              ),
            data: {title: 'products.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/products/product-form/product-form').then(
                (m) => m.ProductForm,
              ),
            data: {title: 'products.addProduct', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/products/product-form/product-form').then(
                (m) => m.ProductForm,
              ),
            data: {title: 'products.editProduct', mode: 'edit'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/products/product-view/product-view').then(
                (m) => m.ProductView,
              ),
            data: {title: 'products.viewProduct', mode: 'view'},
          },
        ],
      },
      {
        path: 'shop',
        canActivate: [clientOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/shop/shop-catalog/shop-catalog').then(
                (m) => m.ShopCatalog,
              ),
            data: {title: 'shop.title'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/shop/shop-product-view/shop-product-view').then(
                (m) => m.ShopProductView,
              ),
            data: {title: 'shop.viewProduct'},
          },
          {
            path: 'cart',
            loadComponent: () =>
              import('@/app/features/cart/cart-page/cart-page').then(
                (m) => m.CartPage,
              ),
            data: {title: 'cart.shoppingCart'},
          },
        ],
      },
      {
        path: 'orders',
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/orders/orders-list/orders-list').then(
                (m) => m.OrdersList,
              ),
            data: {title: 'orders.title'},
          },
          {
            path: 'checkout',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/orders/checkout-page/checkout-page').then(
                (m) => m.CheckoutPage,
              ),
            data: {title: 'orders.checkout'},
          },
          {
            path: 'recurring-checkout',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/orders/recurring-checkout/recurring-checkout').then(
                (m) => m.RecurringCheckoutPage,
              ),
            data: {title: 'orders.recurringCheckout.title'},
          },
          {
            path: 'series/:id',
            loadComponent: () =>
              import('@/app/features/orders/series-details/series-details').then(
                (m) => m.SeriesDetails,
              ),
            data: {title: 'orders.series.detailsTitle'},
          },
          {
            path: ':id',
            loadComponent: () =>
              import('@/app/features/orders/order-details/order-details').then(
                (m) => m.OrderDetails,
              ),
            data: {title: 'orders.details'},
          },
        ],
      },
      {
        path: 'kitchen',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/kitchen/kitchen-report/kitchen-report').then(
                (m) => m.KitchenReport,
              ),
            data: {title: 'kitchen.title'},
          },
          {
            path: 'labels',
            loadComponent: () =>
              import('@/app/features/kitchen/kitchen-labels-list/kitchen-labels-list').then(
                (m) => m.KitchenLabelsList,
              ),
            data: {title: 'kitchen.labels.pageTitle'},
          },
          {
            path: 'labels/:orderId',
            loadComponent: () =>
              import('@/app/features/kitchen/kitchen-label-detail/kitchen-label-detail').then(
                (m) => m.KitchenLabelDetail,
              ),
            data: {title: 'kitchen.labels.detailTitle'},
          },
        ],
      },
      {
        path: 'branches',
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/branches/branches-list/branches-list').then(
                (m) => m.BranchesList,
              ),
            data: {title: 'branches.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/branches/branch-form/branch-form').then(
                (m) => m.BranchForm,
              ),
            data: {title: 'branches.addNewBranch', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/branches/branch-form/branch-form').then(
                (m) => m.BranchForm,
              ),
            data: {title: 'branches.editBranch', mode: 'edit'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/branches/branch-view/branch-view').then(
                (m) => m.BranchView,
              ),
            data: {title: 'branches.viewBranch', mode: 'view'},
          },
        ],
      },
      {
        path: 'clients',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/clients/clients-list/clients-list').then(
                (m) => m.ClientsList,
              ),
            data: {title: 'clients.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/clients/client-form/client-form').then(
                (m) => m.ClientForm,
              ),
            canDeactivate: [clientFormUnsavedGuard],
            data: {title: 'clients.addNewClient', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/clients/client-form/client-form').then(
                (m) => m.ClientForm,
              ),
            canDeactivate: [clientFormUnsavedGuard],
            data: {title: 'clients.editClient', mode: 'edit'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/clients/client-view/client-view').then(
                (m) => m.ClientView,
              ),
            data: {title: 'clients.viewClient', mode: 'view'},
          },
        ],
      },
      {
        path: 'discounts',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/discounts/discounts-list/discounts-list').then(
                (m) => m.DiscountsList,
              ),
            data: {title: 'discounts.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/discounts/discount-form/discount-form').then(
                (m) => m.DiscountForm,
              ),
            data: {title: 'discounts.addDiscount', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/discounts/discount-form/discount-form').then(
                (m) => m.DiscountForm,
              ),
            data: {title: 'discounts.editDiscount', mode: 'edit'},
          },
          {
            path: 'view/:id',
            loadComponent: () =>
              import('@/app/features/discounts/discount-view/discount-view').then(
                (m) => m.DiscountView,
              ),
            data: {title: 'discounts.viewDiscount'},
          },
        ],
      },
      {
        path: 'wallet',
        canActivate: [authGuard],
        loadComponent: () =>
          import('@/app/features/wallet/pages/wallet-overview/wallet-overview').then(
            (m) => m.WalletOverview,
          ),
        data: {title: 'wallet.title'},
      },
      {
        path: 'wallet/manage',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/wallet/pages/wallet-manage/wallet-manage').then(
            (m) => m.WalletManage,
          ),
        data: {title: 'wallet.wallets.title'},
      },
      {
        path: 'wallet/manage/:clientId',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/wallet/pages/wallet-credit-policy/wallet-credit-policy').then(
            (m) => m.WalletCreditPolicy,
          ),
        data: {title: 'wallet.wallets.editPage.title'},
      },
      {
        path: 'returns',
        canActivate: [authGuard],
        loadComponent: () =>
          import('@/app/features/returns/pages/returns-list/returns-list').then(
            (m) => m.ReturnsList,
          ),
        data: {title: 'returns.title'},
      },
      {
        path: 'payments',
        canActivate: [authGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/payments/pages/payments-list/payments-list').then(
                (m) => m.PaymentsList,
              ),
            data: {title: 'payments.title'},
          },
          {
            path: 'method-selector',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/payments/pages/payment-method-selector/payment-method-selector').then(
                (m) => m.PaymentMethodSelectorPage,
              ),
            data: {title: 'payments.methodSelector.title'},
          },
          {
            path: 'submit',
            loadComponent: () =>
              import('@/app/features/payments/pages/bank-transfer-form/bank-transfer-form').then(
                (m) => m.BankTransferForm,
              ),
            data: {title: 'payments.bankTransferForm.title'},
          },
          {
            path: 'card',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/payments/pages/card-payment-form/card-payment-form').then(
                (m) => m.CardPaymentFormPage,
              ),
            data: {title: 'payments.cardPaymentForm.title'},
          },
          {
            path: 'callback',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/payments/pages/payment-callback/payment-callback').then(
                (m) => m.PaymentCallbackPage,
              ),
          },
          {
            path: 'success',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/payments/pages/payment-success/payment-success').then(
                (m) => m.PaymentSuccessPage,
              ),
            data: {title: 'payments.success.title'},
          },
          {
            path: 'failure',
            canActivate: [clientOnlyGuard],
            loadComponent: () =>
              import('@/app/features/payments/pages/payment-failure/payment-failure').then(
                (m) => m.PaymentFailurePage,
              ),
            data: {title: 'payments.failure.title'},
          },
          {
            path: ':id',
            loadComponent: () =>
              import('@/app/features/payments/pages/payment-detail/payment-detail').then(
                (m) => m.PaymentDetail,
              ),
            data: {title: 'payments.detail.title'},
          },
        ],
      },
      {
        path: 'reports',
        canActivate: [tenantOnlyGuard],
        children: [
          {path: '', redirectTo: 'orders', pathMatch: 'full'},
          {
            path: ':type',
            loadComponent: () =>
              import('@/app/features/reports/pages/reports-dashboard/reports-dashboard').then(
                (m) => m.ReportsDashboard,
              ),
            data: {title: 'reports.title'},
          },
        ],
      },
      {
        path: 'client-reports',
        canActivate: [clientOnlyGuard],
        children: [
          {path: '', redirectTo: 'client_orders', pathMatch: 'full'},
          {
            path: ':type',
            loadComponent: () =>
              import('@/app/features/reports/pages/reports-dashboard/reports-dashboard').then(
                (m) => m.ReportsDashboard,
              ),
            data: {title: 'reports.clientTitle'},
          },
        ],
      },
      {
        path: 'settings',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/settings/system-settings/system-settings').then(
            (m) => m.SystemSettings,
          ),
      },
      {
        path: 'gateway-settings',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/settings/gateway-settings/gateway-settings').then(
            (m) => m.GatewaySettings,
          ),
        data: {title: 'gatewaySettings.title'},
      },
      {
        path: 'additional-services',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                '@/app/features/settings/additional-services/additional-services'
              ).then((m) => m.AdditionalServices),
            data: {title: 'additionalServices.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import(
                '@/app/features/settings/additional-services/additional-service-form/additional-service-form'
              ).then((m) => m.AdditionalServiceForm),
            data: {title: 'additionalServices.form.addTitle', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import(
                '@/app/features/settings/additional-services/additional-service-form/additional-service-form'
              ).then((m) => m.AdditionalServiceForm),
            data: {title: 'additionalServices.form.editTitle', mode: 'edit'},
          },
        ],
      },
      {
        path: 'delivery-slots',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/delivery-slots/delivery-slots/delivery-slots').then(
            (m) => m.DeliverySlots,
          ),
        data: {title: 'deliverySlots.title'},
      },
      {
        path: 'drivers',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/drivers/drivers-list/drivers-list').then(
                (m) => m.DriversList,
              ),
            data: {title: 'drivers.title'},
          },
          {
            path: 'add',
            loadComponent: () =>
              import('@/app/features/drivers/driver-form/driver-form').then(
                (m) => m.DriverForm,
              ),
            data: {title: 'drivers.addDriver', mode: 'add'},
          },
          {
            path: 'edit/:id',
            loadComponent: () =>
              import('@/app/features/drivers/driver-form/driver-form').then(
                (m) => m.DriverForm,
              ),
            data: {title: 'drivers.editDriver', mode: 'edit'},
          },
          {
            path: ':id',
            loadComponent: () =>
              import('@/app/features/drivers/driver-details/driver-details').then(
                (m) => m.DriverDetails,
              ),
            data: {title: 'drivers.title'},
          },
        ],
      },
      {
        path: 'holidays',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/holidays/holidays-list/holidays-list').then(
            (m) => m.HolidaysList,
          ),
        data: {title: 'holidays.title'},
      },
      {
        path: 'website',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/landing-pages/admin/landing-admin').then(
            (m) => m.LandingAdminComponent,
          ),
        data: {title: 'landing.websiteSettings'},
      },
      {
        path: 'website-builder',
        canActivate: [tenantOnlyGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@/app/features/landing-builder/admin/website-list.component').then(
                (m) => m.WebsiteListComponent,
              ),
            data: {title: 'websiteBuilder.title'},
          },
          {
            path: ':id',
            loadComponent: () =>
              import('@/app/features/landing-builder/admin/landing-builder-page').then(
                (m) => m.LandingBuilderPageComponent,
              ),
            data: {title: 'landingBuilder.builderPage.title'},
          },
          {
            path: ':id/sections/:sectionId/edit',
            loadComponent: () =>
              import('@/app/features/landing-builder/admin/section-editor/section-editor-page').then(
                (m) => m.SectionEditorPageComponent,
              ),
            data: {title: 'landingBuilder.editorPage.title'},
          },
          {
            path: ':id/settings',
            loadComponent: () =>
              import('@/app/features/landing-builder/admin/components/site-settings/site-settings').then(
                (m) => m.SiteSettingsComponent,
              ),
            data: {title: 'landingBuilder.siteSettings.title'},
          },
          {
            path: ':id/messages',
            loadComponent: () =>
              import('@/app/features/landing-builder/admin/components/messages-inbox/messages-inbox').then(
                (m) => m.MessagesInboxComponent,
              ),
            data: {title: 'landingBuilder.messagesInbox.title'},
          },
        ],
      },
      {
        path: 'organization-info',
        canActivate: [authGuard, tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/organization-info/organization-info/organization-info').then(
            (m) => m.OrganizationInfo,
          ),
        data: {title: 'organizationInfo.title'},
      },
      {
        path: 'my-profile',
        canActivate: [clientOnlyGuard],
        loadComponent: () =>
          import('@/app/features/clients/client-profile/client-profile').then(
            (m) => m.ClientProfile,
          ),
        data: {title: 'clients.myProfile'},
      },
      {
        path: 'contact-us',
        canActivate: [clientOnlyGuard],
        loadComponent: () =>
          import('@/app/features/contact-us/contact-us').then(
            (m) => m.ContactUs,
          ),
        data: {title: 'contactUs.navLabel'},
      },
      {
        path: 'zatca-settings',
        canActivate: [tenantOnlyGuard],
        loadComponent: () =>
          import('@/app/features/zatca/zatca-settings/zatca-settings').then(
            (m) => m.ZatcaSettings,
          ),
        data: {title: 'zatca.zatcaSettings'},
      },
    ],
  },
  {
    path: 'orders/:id/confirm-pickup',
    canActivate: [authGuard, tenantOnlyGuard],
    loadComponent: () =>
      import('@/app/features/orders/confirm-pickup/confirm-pickup').then(
        (m) => m.ConfirmPickup,
      ),
    data: {title: 'orders.confirmPickup.title'},
  },
  {
    path: '**',
    canActivate: [authGuard],
    loadComponent: () =>
      import('@/app/core/pages/not-found/not-found').then((m) => m.NotFound),
  },
];
